import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import Select from 'react-select'
import { Col, FormFeedback, Label, UncontrolledTooltip } from 'reactstrap'
import { handleIsRequired } from '../../utils/formUtils/fields'

export const SelectInput = ({
    inputField,
    isFieldArray,
    formik,
    fieldName,
    error,
    touched,
    inputComponents,
    inputValue,
    index,
    fieldArrayDeleteComponent,
    selectOptions,
    selectOptionsLoading,
    inputsLength,
    inputMargin,
}) => {
    const { t } = useTranslation()

    const optionKey = !isFieldArray ? inputField.name : inputField.oldName
    const rawOptions = selectOptions?.[optionKey] ?? []
    const options = inputField.filterOptions ? inputField.filterOptions(rawOptions, formik) : rawOptions

    const getValue = useCallback(() => {
        if (selectOptions && selectOptions[optionKey]) {
            if (inputField.isMulti) {
                return selectOptions[optionKey]?.filter((option) => inputValue?.indexOf(option.value) >= 0)
            }

            if (selectOptions[optionKey]?.length && selectOptions[optionKey]?.[0].options) {
                let optGroupVal = ''
                let group = selectOptions[optionKey]?.find((option) => option.options.find((o) => o.value === inputValue))
                if (group) {
                    optGroupVal = group.options.find((option) => option.value === inputValue)
                }
                return optGroupVal
            }

            const value = selectOptions[optionKey]?.find((option) => option.value === inputValue?.toString())
            if (value) {
                return selectOptions[optionKey]?.find((option) => option.value === inputValue?.toString())
            }
        }
        return ''
    }, [inputField.isMulti, inputValue, optionKey, selectOptions])

    const isFieldDisabled = inputField.handleInputDisable ? inputField.handleInputDisable(formik, fieldName) : inputField.disabled

    // suppress the spinner once this field's own options have actually arrived (or it's disabled)
    const isLoading = Boolean(selectOptionsLoading) && !isFieldDisabled && rawOptions.length === 0

    return (
        <>
            <Col {...inputField.colProps} className={`${inputField.colProps?.className ?? ''} ${!isFieldArray ? inputMargin : 'mb-2'}`}>
                {inputField.label && (
                    <div className="d-flex justify-content-between">
                        <Label for={fieldName} id={`${inputField.name}Lbl`}>
                            {inputField.label}
                            <span className={`required-mark ${handleIsRequired(inputField, formik, fieldName) ? 'd-inline' : 'd-none'}`}>*</span>
                        </Label>
                        {inputField.tooltip && (
                            <span id={`${inputField.name.toString().replace(/\./g, '')}Tooltip`}>
                                <i className="mdi mdi-help-circle-outline"></i>{' '}
                            </span>
                        )}
                    </div>
                )}

                <Select
                    onChange={(e) => {
                        if (inputField.isMulti) {
                            formik?.setFieldValue(fieldName, e ? e.map((item) => item.value) : [])
                            formik?.setFieldTouched(fieldName, true, false)
                        } else {
                            formik?.setFieldValue(fieldName, e ? e.value : '')
                        }
                        if (inputField.handleChange) {
                            if (inputField.isMulti) {
                                inputField.handleChange(e ? e.map((item) => item.value) : [], formik, fieldName)
                            } else {
                                inputField.handleChange(e?.value, formik, fieldName)
                            }
                        }
                    }}
                    onBlur={() => {
                        formik?.setFieldTouched(fieldName)
                    }}
                    options={options}
                    inputId={fieldName}
                    name={fieldName}
                    value={getValue()}
                    isDisabled={isFieldDisabled}
                    isMulti={inputField.isMulti ?? false}
                    placeholder={inputField.placeholder ?? ''}
                    noOptionsMessage={() => t('form.common.select.noData')}
                    isLoading={isLoading}
                    loadingMessage={() => t('app.common.loading')}
                    className={error && touched ? 'is-invalid' : ''}
                    {...inputField?.inputProps}
                    isClearable={inputField.isClearable}
                />

                {error && touched ? <FormFeedback type="invalid">{error}</FormFeedback> : null}
                {inputField.warning && (
                    <FormFeedback type="invalid" className="d-block mb-2">
                        {inputField.warning}
                    </FormFeedback>
                )}
                {inputComponents && inputComponents[inputField.name] && inputComponents[inputField.name]()}
                {inputField.tooltip && (
                    <UncontrolledTooltip pement="right" target={`${inputField.name.toString().replace(/\./g, '')}Tooltip`}>
                        {inputField.tooltip}
                    </UncontrolledTooltip>
                )}
            </Col>
            {isFieldArray && index === inputsLength && fieldArrayDeleteComponent}
        </>
    )
}
